import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Divider
} from '@mui/material';
import {
  Public,
  Star,
  Schedule,
  TrendingUp,
  Assignment,
  Person
} from '@mui/icons-material';
import { matchService } from '../services/matchService';
import { profileService } from '../services/profileService';
import { useNotification } from '../contexts/NotificationContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [matches, setMatches] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalMatches: 0,
    highMatches: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load profile first
      const profileResponse = await profileService.getProfile();
      setProfile(profileResponse.profile);
      
      // Check if profile is complete enough for matching
      if (!profileResponse.profile || !profileResponse.profile.personal?.age) {
        setLoading(false);
        return;
      }
      
      // Load matches
      const matchResponse = await matchService.getMatches();
      setMatches(matchResponse.matches || []);
      setStats({
        totalMatches: matchResponse.totalMatches || 0,
        highMatches: matchResponse.highMatches || 0
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      showError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationColor = (recommendation) => {
    switch (recommendation) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };

  const getRecommendationText = (recommendation) => {
    switch (recommendation) {
      case 'high': return 'High Match';
      case 'medium': return 'Medium Match';
      case 'low': return 'Low Match';
      default: return 'Unknown';
    }
  };

  const formatPercentage = (score) => {
    return Math.round(score * 100);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Check if profile needs completion
  if (!profile || !profile.personal?.age) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 4, fontWeight: 600 }}>
            Welcome to Your Dashboard
          </Typography>
          
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              To get personalized immigration recommendations, please complete your profile first.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/onboarding')}
              startIcon={<Person />}
            >
              Complete Profile
            </Button>
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
              Immigration Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your personalized immigration recommendations
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/onboarding')}
            startIcon={<Person />}
          >
            Update Profile
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <Public />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="div">
                      {stats.totalMatches}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Matches
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                    <Star />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="div">
                      {stats.highMatches}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      High Matches
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                    <TrendingUp />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="div">
                      {matches.length > 0 ? formatPercentage(matches[0]?.eligibilityScore || 0) : 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Best Match
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                    <Assignment />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="div">
                      {profile.personal?.age || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your Age
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recommendations */}
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          Your Immigration Recommendations
        </Typography>

        {matches.length === 0 ? (
          <Alert severity="info">
            No immigration matches found. Try updating your profile with more information.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {matches.map((match, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate(`/forms/${match.country}/${match.visaType}`)}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {match.country}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {match.visaType}
                        </Typography>
                      </Box>
                      <Chip
                        label={getRecommendationText(match.recommendation)}
                        color={getRecommendationColor(match.recommendation)}
                        size="small"
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Eligibility Score */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          Eligibility Score
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formatPercentage(match.eligibilityScore)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={formatPercentage(match.eligibilityScore)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>

                    {/* Stats */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TrendingUp sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Success Rate: {formatPercentage(match.successRate)}%
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Schedule sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Processing: {match.processingTime}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Action Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/forms/${match.country}/${match.visaType}`);
                      }}
                    >
                      View Form
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Quick Actions */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/onboarding')}
              startIcon={<Person />}
            >
              Update Profile
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/tracking')}
              startIcon={<Assignment />}
            >
              Track Applications
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Dashboard;